import { FC, ReactNode } from 'react'

import Icon from 'project/Icon'
import { PlanType } from 'common/types/enum'
import { CompanySubscription } from 'common/types/responses'
import { useGetNumbersQuery } from 'common/services/useNumber'

type ProductConfig = {
  backgroundColor: string
  icon: React.ReactNode
  title: string
  getDescription?: (subscription?: CompanySubscription) => ReactNode
  isVirtualOffice?: boolean
}

/**
 * Virtual Office plan types - these are the core mail handling plans
 */
export const VIRTUAL_OFFICE_PLAN_TYPES: readonly PlanType[] = [
  PlanType.COLLECT,
  PlanType.SCAN,
  PlanType.SCAN_AND_FORWARD,
  PlanType.FORWARD,
  PlanType.HMRC_FORWARD,
  PlanType.INTERNATIONAL_FORWARD,
  PlanType.HMRC_SCAN,
]

/**
 * Plan types that include mail forwarding capability
 */
export const FORWARDING_PLAN_TYPES: readonly PlanType[] = [
  PlanType.SCAN_AND_FORWARD,
  PlanType.FORWARD,
  PlanType.INTERNATIONAL_FORWARD,
]

/**
 * Check if a plan type is a Virtual Office plan
 */
export const isVirtualOfficePlan = (planType: PlanType | string): boolean =>
  VIRTUAL_OFFICE_PLAN_TYPES.includes(planType as PlanType)

/**
 * Check if a plan type includes forwarding
 */
export const hasForwarding = (planType: PlanType | string): boolean =>
  FORWARDING_PLAN_TYPES.includes(planType as PlanType)

// Registered Office — building grid with checkmark
const RegisteredOfficeIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='10 10 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect
      x='14.1914'
      y='14.1914'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='18.5742'
      y='14.1914'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='22.9531'
      y='14.1914'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='14.1914'
      y='18.5723'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='18.5742'
      y='18.5723'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='22.9531'
      y='18.5723'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='14.1914'
      y='22.9531'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='18.5742'
      y='22.9531'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='22.9531'
      y='22.9531'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='14.1914'
      y='27.334'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <rect
      x='18.5742'
      y='27.334'
      width='2.19046'
      height='2.19046'
      rx='0.518836'
      fill='white'
    />
    <path
      d='M26.1914 11.1426C27.2959 11.1426 28.1913 12.0382 28.1914 13.1426V19.6191C28.1914 19.7769 28.3197 19.9053 28.4775 19.9053H30.5723C31.6768 19.9053 32.5723 20.8007 32.5723 21.9053V30.5723C32.5723 31.6768 31.6768 32.5723 30.5723 32.5723H13.1426C12.0381 32.5721 11.1426 31.6767 11.1426 30.5723V13.1426C11.1427 12.0382 12.0382 11.1427 13.1426 11.1426H26.1914Z'
      stroke='white'
      strokeWidth='1.71429'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M24.0469 27.3347L25.7743 28.6303C26.0258 28.8189 26.3842 28.7575 26.5586 28.4959L29.5233 24.0488'
      stroke='white'
      strokeWidth='1.64298'
      strokeLinecap='round'
    />
  </svg>
)

// Mail Forward — envelope with forward arrow
const MailForwardIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M3 5L12 12L21 5'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <rect
      x='2'
      y='4'
      width='20'
      height='14'
      rx='2'
      stroke='white'
      strokeWidth='1.5'
    />
    <line
      x1='8'
      y1='15'
      x2='16'
      y2='15'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
    <line
      x1='8'
      y1='12'
      x2='13'
      y2='12'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

// Scan — document with magnifier
const ScanIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect
      x='3'
      y='2'
      width='14'
      height='18'
      rx='2'
      stroke='white'
      strokeWidth='1.5'
    />
    <line
      x1='6'
      y1='6'
      x2='14'
      y2='6'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
    <line
      x1='6'
      y1='10'
      x2='12'
      y2='10'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
    <circle cx='16' cy='16' r='4' stroke='white' strokeWidth='1.5' />
    <line
      x1='19'
      y1='19'
      x2='21'
      y2='21'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

// Full Privacy — shield with checkmark
const PrivacyIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 2L4 5.5V11C4 16.25 7.4 21.15 12 22.5C16.6 21.15 20 16.25 20 11V5.5L12 2Z'
      stroke='white'
      strokeWidth='1.5'
      strokeLinejoin='round'
    />
    <path
      d='M8.5 12.5L11 15L16 10'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

// Default — generic product icon (box)
const DefaultProductIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 2L2 7L12 12L22 7L12 2Z'
      stroke='white'
      strokeWidth='1.5'
      strokeLinejoin='round'
    />
    <path
      d='M2 17L12 22L22 17'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M2 12L12 17L22 12'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const getLocationAddress = (subscription?: CompanySubscription): string => {
  const location = subscription?.location
  if (!location) return ''
  const parts = [location.name, location.postal_code].filter(Boolean)
  return parts.join(', ')
}

const WhatsAppActiveNumbers: FC<{ companyId?: number }> = ({ companyId }) => {
  const { data: numbers } = useGetNumbersQuery(
    { company_id: companyId ?? 0 },
    { skip: !companyId },
  )
  const activeCount = numbers?.filter((n) => !n.cancelled_on).length ?? 0
  return <>Active Numbers: {activeCount}</>
}

/**
 * Get the location short name for title suffix (e.g., "HaiRo")
 */
const getLocationName = (subscription?: CompanySubscription): string => {
  return subscription?.location?.short_name || 'HaiRo'
}

export const planTypeConfig: Record<string, ProductConfig> = {
  [PlanType.COLLECT]: {
    backgroundColor: '#E42692',
    getDescription: (subscription) => getLocationAddress(subscription),
    icon: <RegisteredOfficeIcon />,
    isVirtualOffice: true,
    title: 'London Registered Office Address',
  },
  [PlanType.SCAN]: {
    backgroundColor: '#E42692',
    getDescription: (subscription) => getLocationAddress(subscription),
    icon: <RegisteredOfficeIcon />,
    isVirtualOffice: true,
    title: 'London Registered Office Address',
  },
  [PlanType.SCAN_AND_FORWARD]: {
    backgroundColor: '#E42692',
    getDescription: (subscription) => getLocationAddress(subscription),
    icon: <RegisteredOfficeIcon />,
    isVirtualOffice: true,
    title: 'London Registered Office Address',
  },
  [PlanType.FORWARD]: {
    backgroundColor: '#E42692',
    getDescription: (subscription) => getLocationAddress(subscription),
    icon: <RegisteredOfficeIcon />,
    isVirtualOffice: true,
    title: 'London Registered Office Address',
  },
  [PlanType.INTERNATIONAL_FORWARD]: {
    backgroundColor: '#E42692',
    getDescription: (subscription) => getLocationAddress(subscription),
    icon: <RegisteredOfficeIcon />,
    isVirtualOffice: true,
    title: 'London Registered Office Address',
  },
  [PlanType.DIRECTORS_SERVICE_ADDRESS]: {
    backgroundColor: '#3EB8E5',
    getDescription: () => 'Home address privacy service.',
    icon: <PrivacyIcon />,
    title: 'Full Privacy Service Add-on',
  },
  [PlanType.PHONE]: {
    backgroundColor: '#6B7AE8',
    getDescription: () => 'Prime London 020 number',
    icon: <Icon name='phone' fill='white' width={24} height={24} />,
    title: 'Virtual Office Phone',
  },
  [PlanType.WHATSAPP_FOR_BUSINESS_NUMBER]: {
    backgroundColor: '#25D366',
    getDescription: (subscription) => (
      <WhatsAppActiveNumbers companyId={subscription?.company_id} />
    ),
    icon: <Icon name='whatsapp' fill='white' width={24} height={24} />,
    title: 'WhatsApp for Business Phone Number',
  },
  [PlanType.VIRTUALRECEPTIONIST]: {
    backgroundColor: '#6B7AE8',
    getDescription: () => 'Virtual receptionist service',
    icon: <Icon name='phone' fill='white' width={24} height={24} />,
    title: 'Virtual Receptionist',
  },
  [PlanType.COMPANY_FORMATION]: {
    backgroundColor: '#E42692',
    getDescription: () => 'Company formation service',
    icon: <RegisteredOfficeIcon />,
    title: 'Company Formation',
  },
  [PlanType.HMRC_FORWARD]: {
    backgroundColor: '#3E2155',
    getDescription: () => 'HMRC mail forwarding',
    icon: <MailForwardIcon />,
    title: 'HMRC Forwarding',
  },
  [PlanType.HMRC_SCAN]: {
    backgroundColor: '#E42692',
    getDescription: () => 'HMRC mail scanning',
    icon: <ScanIcon />,
    title: 'HMRC Scan',
  },
  [PlanType.OVERAGE_CHARGE]: {
    backgroundColor: '#6E6E73',
    getDescription: () => 'Additional usage charges',
    icon: <DefaultProductIcon />,
    title: 'Overage Charge',
  },
}

const defaultConfig: ProductConfig = {
  backgroundColor: '#6B7AE8',
  getDescription: () => '',
  icon: <DefaultProductIcon />,
  title: '',
}

export const getPlanConfig = (
  planType: PlanType | string,
  subscription?: CompanySubscription,
) => {
  const config = planTypeConfig[planType] ?? {
    ...defaultConfig,
    title: planType,
  }

  // For Virtual Office plans, build title with forwarding and location suffix
  let title = config.title
  let titleSuffix = ''
  if (config.isVirtualOffice && subscription) {
    const includesForwarding = hasForwarding(planType)
    const locationName = getLocationName(subscription)

    if (planType === PlanType.COLLECT) {
      titleSuffix = 'Collect'
    } else {
      if (includesForwarding) {
        title = `${config.title} with Forwarding`
      }
      if (locationName) {
        titleSuffix = `+ ${locationName}`
      }
    }
  }

  return {
    ...config,
    description: config.getDescription?.(subscription) ?? '',
    title,
    titleSuffix,
  }
}

type ProductIconProps = {
  planType: PlanType | string
  className?: string
}

const ProductIcon: FC<ProductIconProps> = ({ className, planType }) => {
  const config = planTypeConfig[planType] ?? defaultConfig

  return (
    <div
      className={`d-flex rounded align-items-center justify-content-center flex-shrink-0 overflow-hidden ${
        className || ''
      }`}
      style={{
        backgroundColor: config.backgroundColor,
        height: 44,
        width: 44,
      }}
    >
      {config.icon}
    </div>
  )
}

export default ProductIcon
